import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  TablePagination,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Divider,
} from '@mui/material';
import { Close, SupportAgent, Search, FilterList, EventNote } from '@mui/icons-material';
import AdminApi from '../../Service/Admin.api';

const SupportTicketsList = () => {
  const [tickets, setTickets] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Detail Modal State
  const [selectedTicket, setSelectedTicket] = useState<any | null>(null);
  const [newStatus, setNewStatus] = useState('');
  const [updatingStatus, setUpdatingStatus] = useState(false);

  // Debounce search input
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setPage(0);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  useEffect(() => {
    fetchTickets();
  }, [page, pageSize, statusFilter, debouncedSearch]);

  const fetchTickets = async () => {
    setLoading(true);
    try {
      const res = await AdminApi.getTickets(page + 1, pageSize, statusFilter, debouncedSearch);
      if (res.success && res.data) {
        setTickets(res.data.data || []);
        setTotalCount(res.data.totalCount || 0);
      }
    } catch (error) {
      console.error('Error fetching tickets:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async () => {
    if (!selectedTicket) return;
    setUpdatingStatus(true);
    try {
      const res = await AdminApi.updateTicketStatus(selectedTicket.id, newStatus);
      if (res.success) {
        setSelectedTicket({ ...selectedTicket, status: newStatus });
        fetchTickets();
      }
    } catch (error) {
      console.error('Error updating ticket status:', error);
    } finally {
      setUpdatingStatus(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'open':
        return 'error';
      case 'in progress':
      case 'inprogress':
        return 'warning';
      case 'resolved':
        return 'success';
      case 'closed':
      default:
        return 'default';
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPageSize(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <SupportAgent sx={{ fontSize: 36, color: '#283165', mr: 1.5 }} />
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1c234b', fontFamily: '"Outfit", sans-serif' }}>
          Support Tickets Management
        </Typography>
      </Box>

      {/* Filters & Search */}
      <Paper sx={{ p: 2.5, mb: 3, borderRadius: 3, boxShadow: '0 4px 20px rgba(0, 0, 0, 0.02)' }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              placeholder="Search by ticket ref, subject, description, user name or email..."
              variant="outlined"
              size="small"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: <Search sx={{ color: 'text.primary', mr: 1, fontSize: 20 }} />,
                sx: { borderRadius: '10px' },
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <FormControl fullWidth size="small">
              <InputLabel sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <FilterList sx={{ fontSize: 18 }} /> Status
              </InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(0);
                }}
                sx={{ borderRadius: '10px' }}
              >
                <MenuItem value="">All Statuses</MenuItem>
                <MenuItem value="Open">Open</MenuItem>
                <MenuItem value="In Progress">In Progress</MenuItem>
                <MenuItem value="Resolved">Resolved</MenuItem>
                <MenuItem value="Closed">Closed</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Data Table */}
      <Paper sx={{ width: '100%', overflow: 'hidden', borderRadius: 3, boxShadow: '0 8px 32px rgba(31, 38, 135, 0.04)', border: '1px solid #f1f5f9' }}>
        {loading && tickets.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}>
            <CircularProgress size={40} sx={{ color: '#283165' }} />
          </Box>
        ) : (
          <>
            <TableContainer sx={{ maxHeight: 'calc(100vh - 290px)' }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f8fafc', color: '#475569' }}>Ticket Ref</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f8fafc', color: '#475569' }}>Date Submitted</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f8fafc', color: '#475569' }}>User Details</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f8fafc', color: '#475569' }}>Category</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f8fafc', color: '#475569' }}>Subject</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f8fafc', color: '#475569' }}>Status</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold', bgcolor: '#f8fafc', color: '#475569' }}>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tickets.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                        <Typography color="text.primary">No support tickets found.</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    tickets.map((row) => (
                      <TableRow key={row.id} hover sx={{ cursor: 'pointer', '&:hover': { bgcolor: '#f8fafc' } }} onClick={() => {
                        setSelectedTicket(row);
                        setNewStatus(row.status);
                      }}>
                        <TableCell sx={{ fontWeight: 'bold', color: '#0f172a' }}>
                          {row.ticketRef}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap', color: '#334155' }}>
                          {formatDate(row.createdAt)}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold" sx={{ color: '#1e293b' }}>
                            {row.userName}
                          </Typography>
                          <Typography variant="caption" color="text.primary" display="block">
                            {row.email}
                          </Typography>
                          <Typography variant="caption" color="text.primary">
                            {row.phoneNumber}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip size="small" label={row.category} variant="outlined" sx={{ fontWeight: 600, borderColor: '#cbd5e1', color: '#475569' }} />
                        </TableCell>
                        <TableCell sx={{ maxWidth: 220 }}>
                          <Typography variant="body2" fontWeight={500} sx={{ color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {row.subject}
                          </Typography>
                          <Typography variant="caption" color="text.primary" sx={{ display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                            {row.description}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip size="small" label={row.status} color={getStatusColor(row.status)} sx={{ fontWeight: 700 }} />
                        </TableCell>
                        <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => {
                              setSelectedTicket(row);
                              setNewStatus(row.status);
                            }}
                            sx={{
                              borderRadius: '8px',
                              textTransform: 'none',
                              borderColor: '#283165',
                              color: '#283165',
                              '&:hover': { bgcolor: 'rgba(40, 49, 101, 0.04)', borderColor: '#1c234b' },
                            }}
                          >
                            View
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={pageSize}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50]}
            />
          </>
        )}
      </Paper>

      {/* Ticket Details Dialog */}
      <Dialog
        open={selectedTicket !== null}
        onClose={() => setSelectedTicket(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: '20px', p: 1 },
        }}
      >
        {selectedTicket && (
          <>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 800, color: '#1e293b', display: 'flex', alignItems: 'center', gap: 1 }}>
                  Ticket Details - {selectedTicket.ticketRef}
                </Typography>
                <Typography variant="caption" color="text.primary">
                  Submitted on {formatDate(selectedTicket.createdAt)}
                </Typography>
              </Box>
              <IconButton onClick={() => setSelectedTicket(null)}>
                <Close />
              </IconButton>
            </DialogTitle>
            <Divider />
            <DialogContent sx={{ py: 3 }}>
              <Grid container spacing={3}>
                {/* User Info Card */}
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#475569', mb: 1 }}>
                    User Details
                  </Typography>
                  <Paper sx={{ p: 2, bgcolor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px' }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#1e293b' }}>
                      Name: <span style={{ fontWeight: 500 }}>{selectedTicket.userName}</span>
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#1e293b', mt: 1 }}>
                      Email: <span style={{ fontWeight: 500 }}>{selectedTicket.email}</span>
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#1e293b', mt: 1 }}>
                      Phone: <span style={{ fontWeight: 500 }}>{selectedTicket.phoneNumber}</span>
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#1e293b', mt: 1 }}>
                      Role: <Chip size="small" label={selectedTicket.userType} color="primary" variant="outlined" sx={{ ml: 1, height: 20, fontSize: '0.75rem', fontWeight: 600 }} />
                    </Typography>
                  </Paper>
                </Grid>

                {/* Ticket metadata Info Card */}
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#475569', mb: 1 }}>
                    Ticket Info
                  </Typography>
                  <Paper sx={{ p: 2, bgcolor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px' }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#1e293b' }}>
                      Category: <span style={{ fontWeight: 500 }}>{selectedTicket.category}</span>
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#1e293b', mt: 1 }}>
                      Case Ref: <span style={{ fontWeight: 500 }}>{selectedTicket.caseNumber || 'N/A'}</span>
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#1e293b', mt: 1, display: 'flex', alignItems: 'center' }}>
                      Current Status: <Chip size="small" label={selectedTicket.status} color={getStatusColor(selectedTicket.status)} sx={{ ml: 1, fontWeight: 700 }} />
                    </Typography>
                  </Paper>
                </Grid>

                {/* Subject & Description */}
                <Grid size={{ xs: 12 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#475569', mb: 1 }}>
                    Subject
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600, color: '#1e293b', mb: 2 }}>
                    {selectedTicket.subject}
                  </Typography>

                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#475569', mb: 1 }}>
                    Description
                  </Typography>
                  <Paper sx={{ p: 2.5, bgcolor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px', whiteSpace: 'pre-wrap' }}>
                    <Typography variant="body2" sx={{ color: '#334155', lineHeight: 1.6 }}>
                      {selectedTicket.description}
                    </Typography>
                  </Paper>
                </Grid>

                {/* Status Update section */}
                <Grid size={{ xs: 12 }} sx={{ mt: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#475569', mb: 1.5 }}>
                    Update Ticket Status
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <FormControl size="small" sx={{ width: 200 }}>
                      <Select
                        value={newStatus}
                        onChange={(e) => setNewStatus(e.target.value)}
                        sx={{ borderRadius: '10px' }}
                      >
                        <MenuItem value="Open">Open</MenuItem>
                        <MenuItem value="In Progress">In Progress</MenuItem>
                        <MenuItem value="Resolved">Resolved</MenuItem>
                        <MenuItem value="Closed">Closed</MenuItem>
                      </Select>
                    </FormControl>
                    <Button
                      variant="contained"
                      disabled={updatingStatus || newStatus === selectedTicket.status}
                      onClick={handleStatusUpdate}
                      sx={{
                        bgcolor: '#283165',
                        color: 'white',
                        px: 3,
                        py: 1,
                        borderRadius: '10px',
                        textTransform: 'none',
                        '&:hover': { bgcolor: '#1c234b' },
                      }}
                    >
                      {updatingStatus ? <CircularProgress size={20} color="inherit" /> : 'Save Status'}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions sx={{ p: 2.5 }}>
              <Button onClick={() => setSelectedTicket(null)} variant="outlined" sx={{ borderRadius: '10px', textTransform: 'none', px: 3 }}>
                Close
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default SupportTicketsList;
